import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { type GeoTrip } from 'types/geo.types';
import { AssetWiseTimelineChart } from './AssetWiseTimelineChart';
import { DumpingTimeLineChart } from './DumpingTimellineChart';
import { transformMasterAlgoToPlanDataByBumperNo } from 'views/FleetPlanner/utils/dataFormatters';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

export const Timeline = ({
  tripsData,
  selectedRange,
  date,
  currentShiftStartUtc,
  currentShiftEndUtc,
}: {
  tripsData: Record<string, GeoTrip[]>;
  selectedRange: string;
  date: dayjs.Dayjs | null;
  currentShiftStartUtc?: string;
  currentShiftEndUtc?: string;
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<'assetwise' | 'dumping'>('assetwise');

  const formattedMasterAlgoDataByBumperNo = useMemo(
    () => transformMasterAlgoToPlanDataByBumperNo(tripsData),
    [tripsData]
  );

  return (
    <Box
      height={'100%'}
      maxHeight={'100%'}
      display={'flex'}
      flexDirection={'column'}
      gap={2}
    >
      <Box
        component="header"
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'start'}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {t('asset_wise_timeline_actual_vs_approved_plan')}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center">
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_, newView) => {
              if (newView) setView(newView);
            }}
          >
            <ToggleButton
              sx={{
                textTransform: 'none',
                padding: '2px 6px',
              }}
              value="assetwise"
            >
              {t('asset_timeline')}
            </ToggleButton>
            <ToggleButton
              sx={{
                textTransform: 'none',
                padding: '2px 6px',
              }}
              value="dumping"
            >
              {t('dumping_timeline')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {view === 'assetwise' ? (
        <AssetWiseTimelineChart
          masterAlgoData={formattedMasterAlgoDataByBumperNo}
          selectedRange={selectedRange}
          currentShiftStartUtc={currentShiftStartUtc}
          currentShiftEndUtc={currentShiftEndUtc}
        />
      ) : (
        <DumpingTimeLineChart
          masterAlgoDataByBumperNo={formattedMasterAlgoDataByBumperNo}
          selectedRange={selectedRange}
          date={date}
          currentShiftStartUtc={currentShiftStartUtc}
          currentShiftEndUtc={currentShiftEndUtc}
        />
      )}
    </Box>
  );
};
